import type { FC, ReactNode, Ref } from 'react'
import type { BandMember, InstrumentKind } from '@/types'

import { cn } from '@/lib/utils'
import { band, members } from '@/constants/portfolio'
import { audioEngine } from '@/lib/audio/engine'
import { scrollToSection } from '@/lib/scroll-to'
import BrushWordmark from '@/components/landing/brush-wordmark'
import CrestMark from '@/components/landing/crest-mark'
import InstrumentArt from '@/components/landing/instrument-art'

export type HeroInstrumentSlot = {
    member: BandMember
    kind: InstrumentKind
    position: string
    width: string
    rotate: number
    direction: [number, number]
    depth: number
    tag: string
    hideOnMobile?: boolean
}

export const HERO_SLOTS: HeroInstrumentSlot[] = [
    {
        member: members[0],
        kind: 'les-paul',
        position: 'left-[5vw] bottom-[38vh] tab:left-[15vw] tab:bottom-[15vw]',
        width: 'w-[17vw] tab:w-[11.5vw]',
        rotate: -18,
        direction: [-1, -0.35],
        depth: 1.3,
        tag: 'right-[-1rem] top-[34%] tab:right-[-3.5rem]'
    },
    {
        member: members[2],
        kind: 'telecaster',
        position: 'right-[5vw] bottom-[38vh] tab:right-[15vw] tab:bottom-[15.5vw]',
        width: 'w-[17vw] tab:w-[11.5vw]',
        rotate: 16,
        direction: [1, -0.35],
        depth: 1.15,
        tag: 'left-[-1rem] top-[30%] tab:left-[-3.5rem]'
    },
    {
        member: members[1],
        kind: 'drum-kit',
        position: 'left-[2vw] bottom-[13vw]',
        width: 'w-[16vw]',
        rotate: -5,
        direction: [-1, 0.45],
        depth: 0.75,
        tag: 'left-[10%] top-[-1.75rem]',
        hideOnMobile: true
    },
    {
        member: members[3],
        kind: 'bass',
        position: 'right-[2.5vw] bottom-[14vw]',
        width: 'w-[8.5vw]',
        rotate: -14,
        direction: [1, 0.45],
        depth: 0.9,
        tag: 'right-[30%] top-[-1.5rem]',
        hideOnMobile: true
    }
]

export const MemberTag: FC<{ member: BandMember, className?: string }> = ({ member, className }): ReactNode => (
    <span className={cn('pointer-events-none absolute z-10 block whitespace-nowrap border-2 border-ink bg-paper-hi px-2 py-1 paper-shadow-sm', className)}>
        <span className='mono-label block text-[9px] text-ink-soft tab:text-[10px]'>
            {String(member.order).padStart(2, '0')} / {member.primaryRole}
        </span>
        <span className='display-type block text-[15px] leading-none tab:text-[19px]'>{member.shortName}</span>
    </span>
)

export const HeroInstrument: FC<{ slot: HeroInstrumentSlot, innerRef?: Ref<HTMLDivElement>, className?: string }> = ({ slot, innerRef, className }): ReactNode => (
    <div
        ref={innerRef}
        className={cn('absolute will-change-transform', slot.position, slot.width, slot.hideOnMobile && 'hidden tab:block', className)}
    >
        <div style={{ transform: `rotate(${slot.rotate}deg)` }}>
            <InstrumentArt kind={slot.kind} />
        </div>
        <MemberTag member={slot.member} className={cn('hidden xs:block', slot.tag)} />
    </div>
)

export const HeroSummary: FC<{ innerRef?: Ref<HTMLDivElement>, className?: string }> = ({ innerRef, className }): ReactNode => (
    <div ref={innerRef} className={cn('max-w-[34ch]', className)}>
        <p className='mono-label text-[11px] text-ink-soft'>Interactive press kit / 2026</p>
        <p className='mt-3 text-[17px] font-medium leading-[1.45] tab:text-[19px]'>
            {band.summary}
        </p>
        <p className='mono-label mt-3 text-[10.5px] text-ink-soft'>
            {band.genres.join(' / ')}
        </p>
    </div>
)

export const ScrollHint: FC<{ innerRef?: Ref<HTMLDivElement>, className?: string }> = ({ innerRef, className }): ReactNode => (
    <div ref={innerRef} className={cn('flex items-center gap-3', className)} aria-hidden='true'>
        <span className='mono-label text-[11px]'>Scroll</span>
        <span className='relative block h-9 w-[3px] overflow-hidden bg-ink/15'>
            <span className='absolute inset-x-0 top-0 h-1/2 animate-[scroll-line_1.6s_cubic-bezier(0.7,0,0.3,1)_infinite] bg-ink' />
        </span>
        <span className='mono-label text-[11px] text-ink-soft'>{band.tagline[2]}</span>
    </div>
)

export const HeroTitle: FC<{ innerRef?: Ref<HTMLHeadingElement>, className?: string }> = ({ innerRef, className }): ReactNode => (
    <h1 ref={innerRef} className={cn('display-type relative select-none', className)}>
        <span aria-hidden='true' className='mono-label absolute -top-6 left-[0.6vw] text-[11px] tracking-[0.3em] tab:-top-8 tab:text-sm'>The</span>
        <span className='sr-only'>The </span>
        <span className='block text-[15.4vw] leading-[0.78] tracking-[-0.055em]'>Bluntsheep</span>
    </h1>
)

export const CrestBack: FC = (): ReactNode => (
    <div className='flex h-full w-full flex-col items-center justify-center rounded-full border-[0.6cqw] border-paper/80 bg-ink text-center text-paper'>
        <span className='display-type block text-[9cqw] leading-[0.95]'>Blunt is<br />light</span>
        <span className='display-type mt-[2cqw] block text-[9cqw] leading-[0.95]'>Blunt is<br />tight</span>
        <span className='mono-label mt-[3cqw] block text-[2.6cqw]'>Choose your path right</span>
    </div>
)

// Flat crest with a CSS 3D back face, used when WebGL is off or unavailable.
export const FlatCrest: FC<{ innerRef?: Ref<HTMLDivElement>, className?: string }> = ({ innerRef, className }): ReactNode => (
    <div className={cn('h-full w-full [container-type:inline-size] [perspective:1100px]', className)}>
        <div ref={innerRef} className='relative h-full w-full [transform-style:preserve-3d]'>
            <div className='absolute inset-0 [backface-visibility:hidden]'>
                <CrestMark className='h-full w-full drop-shadow-[8px_10px_0_rgba(13,13,13,0.85)]' label='The Bluntsheep crest: a deadpan sheep inside a stamped ring' />
            </div>
            <div className='absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]'>
                <CrestBack />
            </div>
        </div>
    </div>
)

export const BrushBand: FC<{ innerRef?: Ref<HTMLDivElement>, className?: string }> = ({ innerRef, className }): ReactNode => (
    <div ref={innerRef} className={cn('bg-ink px-[4vw] py-[2.4vh] paper-shadow', className)} aria-hidden='true'>
        <div className='mx-auto max-w-[78vw] tab:max-w-[62vw]'>
            <BrushWordmark color='#F1EFEA' />
        </div>
        <div className='mx-auto mt-2 flex max-w-[78vw] items-center justify-between gap-4 text-paper/80 tab:max-w-[62vw]'>
            {band.tagline.map(line => (
                <span key={line} className='mono-label text-[9px] tab:text-[11px]'>{line}</span>
            ))}
        </div>
    </div>
)

export const GenrePlate: FC<{ label: string, index: number, innerRef?: Ref<HTMLLIElement> }> = ({ label, index, innerRef }): ReactNode => (
    <li ref={innerRef} className='will-change-transform'>
        <span
            className='block border-2 border-ink bg-paper-hi px-3 py-1.5 paper-shadow-sm'
            style={{ transform: `rotate(${[-3, 2, -1.5, 3][index % 4]}deg)` }}
        >
            <span className='mono-label mr-2 text-[9px] text-ink-soft'>{String(index + 1).padStart(2, '0')}</span>
            <span className='display-type text-[clamp(1rem,1.6vw,1.6rem)]'>{label}</span>
        </span>
    </li>
)

export const BioPlate: FC<{ innerRef?: Ref<HTMLDivElement>, className?: string, pinned?: boolean }> = ({ innerRef, className, pinned }): ReactNode => {
    const facts: Array<[string, string]> = [
        ['Formed', band.founded],
        ['From', band.origin],
        ['Line-up', members.map(member => member.shortName).join(', ')],
        ['Sound', 'Pop rock, alternative, 90s grunge']
    ]

    return (
        <div
            ref={innerRef}
            id='about'
            data-pin-host={pinned ? 'top' : undefined}
            data-pin-progress={pinned ? '0.88' : undefined}
            className={cn('plate relative w-full max-w-[660px] p-5 xs:p-7 tab:p-8', className)}
        >
            <p className='mono-label text-[11px] text-ink-soft'>About the band</p>
            <h2 className='display-type mt-2 text-[clamp(2rem,4.4vw,3.6rem)]'>Four friends, one band.</h2>
            <p className='mt-3 text-[15px] leading-[1.55] text-ink xs:text-[17px] tab:mt-4 tab:text-[18px] tab:leading-[1.6]'>
                {band.shortBio}
            </p>

            <dl className='mt-4 grid grid-cols-2 gap-x-5 gap-y-3 border-t-2 border-ink pt-4 tab:mt-5 tab:grid-cols-4'>
                {facts.map(([term, detail]) => (
                    <div key={term}>
                        <dt className='mono-label text-[10px] text-ink-soft'>{term}</dt>
                        <dd className='mt-1 text-[14px] font-semibold leading-snug tab:text-[15px]'>{detail}</dd>
                    </div>
                ))}
            </dl>

            <div className='mt-5 flex flex-col gap-3 xs:flex-row tab:mt-6'>
                <a
                    href={band.socialLinks.instagram}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() => audioEngine.cue('confirm')}
                    className='group relative inline-flex min-h-12 items-center justify-center overflow-hidden bg-ink px-5 text-paper'
                >
                    <span aria-hidden='true' className='absolute -inset-y-1 -left-4 w-[calc(100%+32px)] -translate-x-[110%] -skew-x-12 bg-bandana transition-transform duration-300 ease-out group-hover:translate-x-0 group-focus-visible:translate-x-0' />
                    <span className='relative text-[15px] font-bold'>Book via Instagram DM</span>
                    <span className='sr-only'> (opens in a new tab)</span>
                </a>
                <a
                    href='#members'
                    onClick={event => {
                        event.preventDefault()
                        audioEngine.cue('confirm')
                        scrollToSection('members')
                    }}
                    className='inline-flex min-h-12 items-center justify-center border-2 border-ink px-5 text-[15px] font-bold transition-colors hover:bg-ink hover:text-paper'
                >
                    Meet the members
                </a>
            </div>
        </div>
    )
}
